import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'sharedUi/Tag';
import { Tooltip } from 'sharedUi/Tooltip';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { ExportButton } from 'sharedUi/ExportButton';
import { logMasterService } from '../../services/log-master.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useDebounce } from '../../hooks/useDebounce';
import type { LogMaster, LogMasterQueryParams } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { ProjectCell } from '../../components/common/project-cell';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'logCode', header: 'Event Code', filter: true },
  { field: 'description', header: 'Description', filter: true },
];

const LogMasterList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const navigate = useAppNavigate();
  const { navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'log-master',
    basePath: '/log-master',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.LOG_MASTER) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.LOG_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.LOG_MASTER);
  const [logMasters, setLogMasters] = useState<(LogMaster & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showGlobal, setShowGlobal] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter by project.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Server-side sort
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  // Fetch log masters
  const fetchLogMasters = useCallback(
    async (
      page: number = 1,
      size: number = pageSize,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);

        const params: LogMasterQueryParams = {
          page,
          page_size: size,
          layer: selectedLayer || undefined,
          search: debouncedSearchText || undefined,
        };

        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showGlobal;

        const response = await logMasterService.getAllPaginated(params);

        const mappedData = (response.items || []).map((item) => ({ ...item, id: item._id }));
        setLogMasters(mappedData);
        setTotalRecords(response.total || 0);
      } catch (err: any) {
        console.error('Failed to fetch log masters:', err);
        showError(extractErrorMessage(err, 'Failed to load log masters. Please try again.'));
      } finally {
        setLoading(false);
      }
    },
    [selectedLayer, debouncedSearchText, selectedProjectId, showGlobal, pageSize, filters, showError]
  );

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchLogMasters(newPage, event.rows);
  };

  const handleSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder as 1 | -1 | null);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchLogMasters(1, pageSize, next);
  };

  useEffect(() => {
    setFirst(0);
    fetchLogMasters(1, pageSize);
  }, [selectedLayer, debouncedSearchText, selectedProjectId, showGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => navigateToView(String(id));
  const handleEdit = (id: string | number) => navigateToEdit(String(id));
  const handleDeleteClick = (id: string | number) => {
    setSelectedId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await logMasterService.delete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchLogMasters();
    } catch (err: any) {
      console.error('Failed to delete log master:', err);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(err, 'Failed to delete log master'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  // Layer filter — codes must match the `auditLayer` value-set (BE does an exact match)
  const layerOptions = [
    { label: 'All Layers', value: '' },
    { label: 'API', value: 'API' },
    { label: 'Screen', value: 'Screen' },
    { label: 'Function', value: 'Function' },
    { label: 'Config', value: 'Config' },
  ];
  // Level/Type filters — sit in the toolbar next to Layer; both flow through the
  // per-column `filters` state (exact match), which the BE applies server-side.
  const levelOptions = [
    { label: 'All Levels', value: '' },
    { label: 'INFO', value: 'INFO' },
    { label: 'WARNING', value: 'WARNING' },
    { label: 'ERROR', value: 'ERROR' },
  ];
  const typeOptions = [
    { label: 'All Types', value: '' },
    { label: 'AUDIT', value: 'AUDIT' },
    { label: 'USAGE', value: 'USAGE' },
    { label: 'SECURITY', value: 'SECURITY' },
  ];
  const setColFilter = (field: string, value: string) =>
    handleFilter({ filters: { ...(filters || {}), [field]: { value: value || null, matchMode: 'equals' } } });

  // Export columns configuration
  const exportColumns = useMemo(() => [
    { field: 'logCode', header: 'Event Code' },
    { field: 'description', header: 'Description' },
    { field: 'messageTemplate', header: 'Message Template' },
    { field: 'category', header: 'Category' },
    { field: 'layer', header: 'Layer' },
    { field: 'logLevel', header: 'Log Level' },
    { field: 'logtype', header: 'Log Type' },
  ], []);

  const fetchExportData = useCallback(async (): Promise<LogMaster[]> => {
    const params: Omit<LogMasterQueryParams, 'page' | 'page_size'> = {};
    if (selectedLayer) params.layer = selectedLayer;
    if (debouncedSearchText?.trim()) params.search = debouncedSearchText.trim();
    const filtersParam = buildColumnFiltersParam(filters);
    if (filtersParam) params.filters = filtersParam;
    return logMasterService.getAllForExport(params, pageSize);
  }, [selectedLayer, debouncedSearchText, pageSize, filters]);

  const getLevelSeverity = (level: string): 'info' | 'warning' | 'danger' | 'secondary' => {
    switch (level?.toUpperCase()) {
      case 'INFO': return 'info';
      case 'WARNING': case 'WARN': return 'warning';
      case 'ERROR': return 'danger';
      default: return 'secondary';
    }
  };

  const columns: SaasColumn<LogMaster & { id: string }>[] = useMemo(() => {
    const baseColumns: SaasColumn<LogMaster & { id: string }>[] = [
      {
        field: 'logCode',
        header: 'Event Code',
        sortable: true,
        filter: true,
        hideable: false,
        style: { minWidth: '280px' },
        body: (rowData) => (
          <span className="font-mono text-xs text-ink break-all">
            {rowData.logCode || '-'}
          </span>
        ),
      },
      {
        field: 'description',
        header: 'Description',
        filter: true,
        style: { minWidth: '250px' },
        body: (rowData) => (
          <span
            className="text-xs text-ink-muted line-clamp-1"
            data-pr-tooltip={rowData.description || undefined}
          >
            {rowData.description || '-'}
          </span>
        ),
      },
      {
        field: 'layer',
        header: 'Layer',
        sortable: true,
        style: { width: '80px' },
        body: (rowData) => (
          <span className="text-xs text-ink-muted">
            {rowData.layer || '-'}
          </span>
        ),
      },
      {
        field: 'logLevel',
        header: 'Level',
        sortable: true,
        style: { width: '90px' },
        body: (rowData) => (
          <Tag value={rowData.logLevel || 'INFO'} severity={getLevelSeverity(rowData.logLevel)} className="text-xs" />
        ),
      },
      {
        field: 'logtype',
        header: 'Type',
        sortable: true,
        style: { width: '90px' },
        body: (rowData) => (
          <span className="text-xs text-ink-muted">
            {rowData.logtype || '-'}
          </span>
        ),
      },
      {
        field: 'parameters',
        header: 'Parameters',
        style: { width: '120px' },
        body: (rowData) => {
          const params = rowData.parameters || [];
          const keyCount = params.filter((p) => p.isKeyField).length;
          const sensitiveCount = params.filter((p) => p.isSensitive).length;
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-gray-700 dark:text-gray-300">
                {params.length} field{params.length !== 1 ? 's' : ''}
              </span>
              {keyCount > 0 && (
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  {keyCount} key
                </span>
              )}
              {sensitiveCount > 0 && (
                <span className="text-xs text-red-500 dark:text-red-400">
                  {sensitiveCount} sensitive
                </span>
              )}
            </div>
          );
        },
      },
    ];

    baseColumns.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    baseColumns.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (rowData) => {
        const isDeleted = rowData.isDelete;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(rowData.id)}
              data-testid="LogMasterList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
            </button>
            {!isDeleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(rowData.id)}
                    data-testid="LogMasterList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(rowData.id)}
                    data-testid="LogMasterList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return baseColumns;
  }, [canUpdate, canDelete, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.LOG_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-LogMaster-List">
        {!embedded && (
          <PageHeader
            icon="pi pi-file-edit"
            title="Log Master"
            subtitle="Manage log configurations and templates"
            testId="LogMaster.Header"
          />
        )}

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="log-master"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={logMasters}
            loading={loading}
            dataKey="id"
            emptyMessage="No log masters found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search event code, description..."
            testId="LogMasterList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="LogMasterList.Filter.Project"
                  />
                )}
                {/* Always available — only the project PICKER is gated by >1 project. */}
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  <InputSwitch
                    checked={showGlobal}
                    onChange={(e: any) => setShowGlobal(!!e.value)}
                    data-testid="LogMasterList.Toggle.ShowGlobal"
                  />
                  Show core
                </label>
                <Dropdown
                  value={selectedLayer}
                  options={layerOptions}
                  onChange={(e: { value: string }) => setSelectedLayer(e.value || '')}
                  placeholder="All Layers"
                  style={{ width: '150px' }}
                  data-testid="LogMasterList.Filter.Layer"
                  resetFilterOnHide={true}
                />
                <Dropdown
                  value={filters?.logLevel?.value ?? ''}
                  options={levelOptions}
                  onChange={(e: { value: string }) => setColFilter('logLevel', e.value)}
                  placeholder="All Levels"
                  style={{ width: '150px' }}
                  data-testid="LogMasterList.Filter.Level"
                  resetFilterOnHide={true}
                />
                <Dropdown
                  value={filters?.logtype?.value ?? ''}
                  options={typeOptions}
                  onChange={(e: { value: string }) => setColFilter('logtype', e.value)}
                  placeholder="All Types"
                  style={{ width: '150px' }}
                  data-testid="LogMasterList.Filter.Type"
                  resetFilterOnHide={true}
                />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <ExportButton
                  fetchData={fetchExportData}
                  columns={exportColumns}
                  filename="log-masters"
                  testId="LogMasterList.Button.Export"
                />
                <RefreshButton
                  onClick={() => fetchLogMasters(Math.floor(first / pageSize) + 1, pageSize)}
                  loading={loading}
                  testId="LogMasterList.Button.Refresh"
                />
                {canCreate && (
                  <Button
                    label="Create Log Master"
                    icon="pi pi-plus"
                    onClick={() => navigate('/log-master/create')}
                    data-testid="LogMasterList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>
        <Tooltip target="[data-pr-tooltip]" />

        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this log master? This action will mark it as deleted."
          testId="LogMasterList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default LogMasterList;
